import { useState, useEffect, useCallback } from "react";
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog, Droplets, Wind, Thermometer, MapPin, Sunrise, Sunset } from "lucide-react";

/* ── Defiance, Ohio ── */
const LAT = 41.2845;
const LON = -84.3558;

const WEATHER_CODES = {
  0: "CLEAR SKY", 1: "MAINLY CLEAR", 2: "PARTLY CLOUDY", 3: "OVERCAST",
  45: "FOGGY", 48: "RIME FOG", 51: "LIGHT DRIZZLE", 53: "DRIZZLE",
  55: "HEAVY DRIZZLE", 61: "LIGHT RAIN", 63: "RAIN", 65: "HEAVY RAIN",
  71: "LIGHT SNOW", 73: "SNOW", 75: "HEAVY SNOW",
  80: "SHOWERS", 81: "MOD SHOWERS", 82: "HEAVY SHOWERS",
  95: "THUNDERSTORM", 96: "T-STORM + HAIL", 99: "SEVERE STORM",
};

const getWeatherIcon = (code) => {
  if (code === 0 || code === 1) return Sun;
  if (code === 2 || code === 3) return Cloud;
  if (code >= 45 && code <= 48) return CloudFog;
  if (code >= 51 && code <= 55) return CloudDrizzle;
  if (code >= 61 && code <= 67) return CloudRain;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 85 && code <= 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  return Cloud;
};

const getIconColor = (code) => {
  if (code === 0 || code === 1) return "text-amber-400";
  if (code >= 95) return "text-amber-300";
  return "text-cyan-300";
};

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [isLive, setIsLive] = useState(false);

  const fetchWeather = useCallback(async () => {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,visibility&daily=temperature_2m_max,temperature_2m_min,sunrise,sunset&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America/New_York&forecast_days=1`;
      const resp = await fetch(url);
      const raw = await resp.json();
      const current = raw.current || {};
      const daily = raw.daily || {};
      const code = current.weather_code || 0;

      setWeather({
        location: "Defiance, Ohio",
        temp: Math.round(current.temperature_2m || 0),
        feels_like: Math.round(current.apparent_temperature || 0),
        humidity: current.relative_humidity_2m || 0,
        wind: Math.round(current.wind_speed_10m || 0),
        visibility: Math.round((current.visibility || 10000) / 1609.34 * 10) / 10,
        condition: WEATHER_CODES[code] || "UNKNOWN",
        weather_code: code,
        high: Math.round((daily.temperature_2m_max || [0])[0]),
        low: Math.round((daily.temperature_2m_min || [0])[0]),
        sunrise: (daily.sunrise || [""])[0],
        sunset: (daily.sunset || [""])[0],
      });
      setIsLive(true);
    } catch (e) {
      console.error("Weather fetch error:", e);
      setIsLive(false);
    }
  }, []);

  useEffect(() => {
    fetchWeather();
    const iv = setInterval(fetchWeather, 600000); // 10 min
    return () => clearInterval(iv);
  }, [fetchWeather]);

  const w = weather || {};
  const WeatherIcon = getWeatherIcon(w.weather_code || 0);
  const iconColor = getIconColor(w.weather_code || 0);

  const fmtTime = (iso) => {
    if (!iso) return "--";
    try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }); }
    catch { return "--"; }
  };

  return (
    <div data-testid="weather-widget" className="bg-gray-950/80 border border-cyan-500/20 rounded-sm p-3 flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center gap-1.5 border-b border-cyan-500/15 pb-2">
        <Thermometer className="w-3 h-3 text-cyan-400 shrink-0" />
        <span className="font-heading text-[10px] tracking-[0.15em] uppercase text-cyan-300">WEATHER</span>
        <div className="ml-auto flex items-center gap-0.5 shrink-0">
          <MapPin className="w-2.5 h-2.5 text-cyan-600" />
          <span className="font-mono text-[7px] tracking-wider text-cyan-600 truncate max-w-[60px]">DEFIANCE</span>
        </div>
      </div>

      {/* Temperature row */}
      <div className="flex items-center gap-2">
        <WeatherIcon className={`w-6 h-6 shrink-0 ${iconColor}`} style={{ filter: "drop-shadow(0 0 3px rgba(251,191,36,0.4))" }} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xl text-cyan-200 leading-none">
            {w.temp ?? "--"}<span className="text-[10px] text-cyan-500">F</span>
          </div>
          <div className="font-mono text-[8px] tracking-wider text-cyan-600 truncate">{w.condition || "LOADING"}</div>
        </div>
        <div className="flex flex-col items-end shrink-0 font-mono text-[8px] text-cyan-600 leading-relaxed">
          <span>H:<span className="text-amber-400 ml-0.5">{w.high ?? "--"}F</span></span>
          <span>L:<span className="text-cyan-400 ml-0.5">{w.low ?? "--"}F</span></span>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-col gap-1.5">
        <StatRow icon={<Droplets className="w-2.5 h-2.5" />} label="HUMID" value={w.humidity != null ? `${w.humidity}%` : "--"} />
        <StatRow icon={<Wind className="w-2.5 h-2.5" />} label="WIND" value={w.wind != null ? `${w.wind}mph` : "--"} />
        <StatRow icon={<Sunrise className="w-2.5 h-2.5" />} label="RISE" value={fmtTime(w.sunrise)} />
        <StatRow icon={<Sunset className="w-2.5 h-2.5" />} label="SET" value={fmtTime(w.sunset)} />
      </div>

      {/* Live indicator */}
      <div className="pt-1.5 border-t border-cyan-500/10 flex items-center justify-center gap-1">
        <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.8)]" : "bg-cyan-700"}`} />
        <span className={`font-mono text-[7px] tracking-wider ${isLive ? "text-emerald-400" : "text-cyan-700"}`}>{isLive ? "LIVE" : "OFFLINE"}</span>
      </div>
    </div>
  );
};

const StatRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-1.5 font-mono text-[8px] tracking-wider">
    <span className="text-cyan-600 shrink-0">{icon}</span>
    <span className="text-cyan-700">{label}:</span>
    <span className="text-cyan-400 ml-auto">{value}</span>
  </div>
);

export default WeatherWidget;
